<template>
  <div>
    <b-modal ref="secureDocModal" centered lazy scrollable @hide="hideModal" size="lg">
      <template #modal-title>
        <div class="d-flex align-items-center">
          <span>{{ documentTitle }}</span>
          <b-badge v-if="isRejected" variant="danger" class="ml-2">Rejected</b-badge>
        </div>
      </template>
      <b-skeleton-img class="w-100" height="500px" v-if="isUrlLoading || isLoading" no-aspect></b-skeleton-img>

    <div v-else-if="!isUrlLoading && fileSrc">
      <div class="alert alert-warning w-100 mb-2" role="alert" v-if="expireAt && isExpired">
        This document has been expired.
      </div>

      <div class="alert alert-danger w-100 mb-2" role="alert" v-if="isRejected">
        <div class="d-flex align-items-start">
          <md-icon class="mr-2">block</md-icon>
          <div>
            <div class="font-weight-bold">This document is rejected.</div>
            <div class="small" v-if="rejectionReason">Reason: {{ rejectionReason }}</div>
          </div>
        </div>
      </div>

      <div v-if="expireAt" class="d-flex justify-content-between align-items-center">
        <span> {{ isExpired ? 'Expired' : 'Expires' }} on: {{ formatDateSimple(expireAt) }}</span>
      </div>

      <img :src="imageSrc" v-if="(!isLoading && getFileType()) === 'img'" class="w-100 mt-2" />
      <div v-else-if="getFileType() === 'pdf'" v-for="page in numPages" :key="page">
        <pdf :src="pdfSrc" :page="page" class="w-100 mt-2"></pdf>
        <hr v-if="page < numPages" />
      </div>
    </div>
    <span v-else-if="!isLoading && fileSrc"
      >Uploaded file isn't supported for viewing.Click download button to download the file.
      <!-- <a href="#" class="text-primary" @click.prevent="downloadGivenFile">Click to download.</a> -->
    </span>

    <div class="card p-3 mt-3" v-if="questions && questions.length">
      <b-form-group
        :label="question.question"
        label-class="form-label"
        v-for="(question, index) in questions"
        :key="index"
      >
        <b-form-textarea
          class="form-control"
          rows="5"
          :placeholder="question.question"
          :value="question.answer"
          disabled
        ></b-form-textarea>
      </b-form-group>
    </div>

      <template #modal-footer>
        <div class="w-100 d-flex justify-content-end align-items-center">
          <!-- <b-btn variant="outline-secondary" @click="hideModal" class="mr-2" :disabled="isRejecting">Close</b-btn> -->

          <b-btn
            variant="primary"
            class="mr-2"
            v-if="fileSrc && !isLoading"
            @click="downloadGivenFile"
          >
            Download
          </b-btn>

          <b-btn v-if="!isRejected" variant="danger" @click="openRejectModal" :disabled="isRejecting">Reject</b-btn>
        </div>
      </template>
    </b-modal>

    <b-modal
      ref="rejectReasonModal"
      centered
      :hide-header-close="false"
      title="Reject document"
      @hidden="rejectReason = ''"
    >
      <div class="mb-2 text-muted small">Provide a clear reason so the student knows what to fix.</div>
      <b-form-textarea
        v-model="rejectReason"
        class="form-control"
        rows="4"
        placeholder="E.g., Document is blurry. Please upload a clearer scan."
      ></b-form-textarea>

      <template #modal-footer>
        <div class="w-100 d-flex justify-content-end">
          
          <b-btn variant="danger" @click="submitReject" :disabled="isRejecting || !rejectReason.trim()">
            <b-spinner small v-if="isRejecting"></b-spinner>
            {{ isRejecting ? 'Rejecting...' : 'Reject' }}
          </b-btn>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import mime from 'mime';
import pdf from '@teckel/vue-pdf';
import { get } from 'lodash';
import { parseFileNameFromUrl, formatDateSimple, isDateExpired } from '@/common/utils';
import { mapActions } from 'vuex';

export default {
  name: 'SecureDocModal',

  components: { pdf },

  props: {
    showModal: { type: Boolean, default: false },
    documentTitle: { type: String, default: '' },
    id: { type: Number, default: null },
    documentType: { type: String, default: '' },
  },

  data() {
    return {
      isLoading: false,
      imageSrc: null,
      pdfSrc: null,
      numPages: 1,
      showSecurityModal: false,
      isUrlLoading: false,
      fileSrc: null,
      expireAt: null,
      isExpired: false,
      questions: [],
      rejectReason: '',
      isRejecting: false,
      piiId: null,
      isRejected: false,
      rejectionReason: null,
      rejectedAt: null,
    };
  },

  computed: {},

  watch: {
    async showModal(value) {
      if (value) {
        await this.fetchStudentDocUrl();

        this.$refs.secureDocModal.show();
      }
    },
    expireAt(value) {
      if (value) {
        this.isExpired = isDateExpired(this.expireAt);
      }
    },
  },

  methods: {
    get,
    ...mapActions('fileDownload', ['downloadFile']),
    ...mapActions('student', ['getStudentDocUrl', 'rejectStudentPii']),
    formatDateSimple,
    async fetchStudentDocUrl() {
      this.isUrlLoading = true;
      try {
        const res = await this.getStudentDocUrl({ student_id: this.id, type: this.documentType });
        this.piiId = res.data.id;
        this.fileSrc = res.data.url;
        this.expireAt = res.data.expire_at;
        this.questions = get(res, 'data.questions_answers');
        this.isRejected = res.data.is_rejected || false;
        this.rejectionReason = res.data.rejection_reason;
        this.rejectedAt = res.data.rejected_at;
        if (['img', 'pdf'].includes(this.getFileType())) {
          this.isLoading = true;
          if (this.getFileType() === 'img') {
            this.loadImage();
          } else {
            this.pdfSrc = pdf.createLoadingTask(this.fileSrc);
            this.pdfSrc.promise.then((loadedPdf) => {
              this.numPages = loadedPdf.numPages;
              this.isLoading = false;
            });
          }
        }
      } catch (err) {
        if (get(err.response, 'status') === 403) {
          this.$emit('verification', this.documentTitle, this.id, this.documentType);
          this.hideModal();
        } else this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isUrlLoading = false;
    },
    hideModal() {
      this.$emit('close');
      setTimeout(() => this.$refs.secureDocModal.hide());

      if (this.$refs.rejectReasonModal) this.$refs.rejectReasonModal.hide();

      this.imageSrc = this.pdfSrc = null;
      this.numPages = 1;
      this.isLoading = false;
      this.rejectReason = '';
      this.piiId = null;
      this.isRejected = false;
      this.rejectionReason = null;
      this.rejectedAt = null;
    },

    hideModalManual() {
      this.$refs.secureDocModal.hide();
      this.hideModal();
    },

    getFileType() {
      if (!this.fileSrc) return;
      const type = mime.getType(parseFileNameFromUrl(this.fileSrc).split('.').pop());

      if (type && type.includes('image')) {
        return 'img';
      } else if (type && type.includes('pdf')) {
        return 'pdf';
      }

      return null;
    },

    loadImage() {
      const img = new Image();
      const vm = this;
      img.onload = () => {
        vm.imageSrc = vm.fileSrc;
        vm.isLoading = false;
      };
      img.src = this.fileSrc;
    },

    async downloadGivenFile() {
      const h = this.$createElement;

      this.makeToast({
        variant: 'primary',
        msg: h('span', [h('i', { class: 'fas fa-download mr-2' }), 'Downloading File']),
        toaster: 'b-toaster-bottom-left',
      });

      await this.downloadFile({ fileUrl: this.fileSrc, removeTimestamp: true });

      this.makeToast({
        variant: 'success',
        msg: h('span', [h('i', { class: 'fas fa-download mr-2' }), 'File Downloaded']),
        toaster: 'b-toaster-bottom-left',
      });
    },

    openRejectModal() {
      if (!this.piiId) {
        this.makeToast({ variant: 'danger', msg: 'PII ID is missing' });
        return;
      }
      this.rejectReason = '';
      this.$refs.rejectReasonModal.show();
    },

    async submitReject() {
      if (!this.rejectReason.trim()) {
        this.makeToast({ variant: 'warning', msg: 'Please enter a rejection reason' });
        return;
      }

      if (!this.piiId) {
        this.makeToast({ variant: 'danger', msg: 'PII ID is missing' });
        return;
      }

      this.isRejecting = true;
      try {
        await this.rejectStudentPii({ pii_id: this.piiId, data: { reason: this.rejectReason } });
        this.makeToast({ variant: 'success', msg: 'Student PII rejected successfully' });
        this.$emit('rejected');
        if (this.$refs.rejectReasonModal) this.$refs.rejectReasonModal.hide();
        this.hideModal();
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: get(err, 'response.data.message') || this.$t('generalMsgs.genErrorMsg') });
      } finally {
        this.isRejecting = false;
      }
    },
  },

  async mounted() {},
};
</script>




